import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaEye, FaEyeSlash, FaSearch, FaCloudUploadAlt, FaCloudDownloadAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { COLORS } from '../../../constants/radar';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import EmployeeSearch from '../../common/EmployeeSearch';
import { useAlert } from '../../../contexts/AlertContext';
import Modal from '../../common/Modal';
import ConfirmationModal from '../../common/ConfirmationModal';
import EmptyState from '../../common/EmptyState';
import LoadingSpinner from '../../common/LoadingSpinner';
import { permissionsApi } from '../../../services/api/permissionsApi';
import { DynamicIcon } from '../Roles/utils/iconMap';

// Import the same card styles from Roles
import { MainContainer, RolesGrid, RoleCard, RoleCardContent, RoleDetailsGrid, RoleButtons } from '../Roles/styles';
import { toLocalDate } from '../../../utils/dateOnly';

const EmployeeRoles = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [searchTerm, setSearchTerm] = useState('');
  const [showControls, setShowControls] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('assign'); // 'assign' or 'edit'
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    employeeAccessRole: null,
    deleting: false
  });

  // Load employee access roles and available roles on component mount
  useEffect(() => {
    loadEmployeeAccessRoles();
    loadRoles();
  }, []);

  const loadEmployeeAccessRoles = useCallback(async () => {
    try {
      setLoading(true);
      const employeeAccessRoles = await permissionsApi.getAllEmployeeAccessRoles();
      setEmployees(employeeAccessRoles);
    } catch (error) {
      console.error('Error loading employee access roles:', error);
      showAlert(error.message || 'Failed to load individual access roles', 'error');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  const loadRoles = useCallback(async () => {
    try {
      const rolesData = await permissionsApi.getAllRoles({ published: true });
      setRoles(rolesData);
    } catch (error) {
      console.error('Error loading roles:', error);
      showAlert(error.message || 'Failed to load roles', 'error');
      setRoles([]);
    }
  }, [showAlert]);

  const filteredEmployees = employees.filter(employeeAccessRole => 
    employeeAccessRole.employee.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employeeAccessRole.employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employeeAccessRole.role.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get list of available roles
  const availableRoles = roles.filter(role => role.isPublished);

  // Get already assigned employee IDs
  const assignedEmployeeIds = employees.map(emp => emp.employee.id);

  const getStatusColor = (isPublished) => {
    return isPublished ? '#E6F4EA' : '#F3F4F6';
  };

  const getStatusTextColor = (isPublished) => {
    return isPublished ? '#1E8E3E' : '#6B7280';
  };

  const getStatusText = (isPublished) => {
    return isPublished ? 'Published' : 'Draft';
  };

  const handleEditEmployee = (employeeAccessRole) => {
    setModalMode('edit');
    setEditingEmployeeId(employeeAccessRole.id);
    setSelectedEmployee(employeeAccessRole.employee);
    setSelectedRole(employeeAccessRole.role.id);
    setModalOpen(true);
  };

  const handlePublishToggle = async (employeeAccessRoleId, currentPublishState) => {
    try {
      if (currentPublishState) {
        await permissionsApi.unpublishEmployeeAccessRole(employeeAccessRoleId);
        showAlert('Individual role unpublished successfully', 'success');
      } else {
        await permissionsApi.publishEmployeeAccessRole(employeeAccessRoleId);
        showAlert('Individual role published successfully', 'success');
      }
      // Reload the data to get the updated state
      await loadEmployeeAccessRoles();
    } catch (error) {
      console.error('Error toggling publish state:', error);
      showAlert(error.message || 'Failed to update publish state', 'error');
    }
  };

  const handleDeleteClick = (employeeAccessRole) => {
    setDeleteModal({
      open: true,
      employeeAccessRole,
      deleting: false
    });
  };

  const handleDelete = async () => {
    if (!deleteModal.employeeAccessRole) return;
    
    try {
      setDeleteModal(prev => ({ ...prev, deleting: true }));
      
      await permissionsApi.deleteEmployeeAccessRole(deleteModal.employeeAccessRole.id);
      
      showAlert('Individual role removed successfully', 'success');
      // Reload the data to get the updated state
      await loadEmployeeAccessRoles();
    } catch (error) {
      console.error('Error deleting employee role:', error);
      showAlert(error.message || 'Failed to remove individual role', 'error');
    } finally {
      // Close the modal
      setDeleteModal({ open: false, employeeAccessRole: null, deleting: false });
    }
  };

  const handleOpenAssignModal = () => {
    setModalMode('assign');
    setModalOpen(true);
    setSelectedEmployee(null);
    setSelectedRole('');
    setEditingEmployeeId(null);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEmployee(null);
    setSelectedRole('');
    setEditingEmployeeId(null);
  };

  const handleSaveRole = async () => {
    if (!selectedRole) {
      showAlert('Please select a role', 'error');
      return;
    }

    try {
      if (modalMode === 'assign') {
        if (!selectedEmployee) {
          showAlert('Please select an individual', 'error');
          return;
        }

        // Check if employee already has a role
        const existingEmployee = employees.find(emp => emp.employee.email === selectedEmployee.email);
        if (existingEmployee) {
          showAlert('This individual already has a role assigned', 'error');
          return;
        }

        // Create new employee access role
        await permissionsApi.createEmployeeAccessRole({
          employeeId: selectedEmployee.id,
          roleId: selectedRole
        });
        showAlert('Individual role assigned successfully', 'success');
        
      } else {
        // Edit mode - update existing employee role
        await permissionsApi.updateEmployeeAccessRole(editingEmployeeId, {
          roleId: selectedRole
        });
        showAlert('Individual role updated successfully', 'success');
      }

      // Reload the data to get the updated state
      await loadEmployeeAccessRoles();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving employee role:', error);
      showAlert(error.message || 'Failed to save individual role', 'error');
    }
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/permissions')}>
            <FaArrowLeft /> Back to Permissions
          </BackButton>
          <Title>Individual Roles</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton onClick={() => setShowControls(!showControls)}>
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <TextField
              placeholder="Search Individual Roles"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                flex: 1,
                maxWidth: '500px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  height: '40px',
                  fontSize: '14px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  }
                },
                '& .MuiInputBase-input': {
                  padding: '8px 14px',
                }
              }}
            />

            <HeaderButton 
              onClick={handleOpenAssignModal}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Assign Individual Role
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer show={showControls}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <LoadingSpinner />
            </Box>
          )}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2
          }}>
            <Typography 
              sx={{ 
                color: '#64748B',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }}>
                {filteredEmployees.length}
              </Box>
              {filteredEmployees.length === 1 ? 'individual' : 'individuals'} found
            </Typography>
          </Box>

          {!loading && filteredEmployees.length === 0 ? (
            <EmptyState
              title="No Individual Roles Found"
              description={searchTerm
                ? "No individuals match your search criteria. Try adjusting your search terms or assign roles to new individuals."
                : "No individual roles have been assigned yet. Start by assigning roles to individuals in your organization."
              }
            />
          ) : (
            !loading && (
              <RolesGrid>
                {filteredEmployees.map((employeeAccessRole) => (
                  <RoleCard key={employeeAccessRole.id}>
                    <RoleCardContent>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        mb: 2,
                        gap: 1
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <EmployeeAvatar 
                            employee={employeeAccessRole.employee}
                            size={48}
                          />
                          <Box>
                            <Typography
                              sx={{ 
                                color: COLORS.PRIMARY,
                                fontSize: '18px',
                                fontWeight: 600,
                                marginBottom: '2px'
                              }}
                            >
                              {employeeAccessRole.employee.fullName}
                            </Typography>
                            <Typography color="#666" variant="body2" sx={{ marginBottom: '4px' }}>
                              {employeeAccessRole.employee.email}
                            </Typography>
                            <Chip
                              label={employeeAccessRole.role.displayName}
                              variant="outlined"
                              icon={<DynamicIcon iconName={employeeAccessRole.role.icon} />}
                              size="small"
                              sx={{ 
                                borderColor: employeeAccessRole.role.color,
                                color: employeeAccessRole.role.color,
                                backgroundColor: 'transparent',
                                fontWeight: 500,
                                fontSize: '12px',
                                '& .MuiChip-icon': {
                                  color: employeeAccessRole.role.color
                                }
                              }}
                            />
                          </Box>
                        </Box>
                        <Chip
                          label={getStatusText(employeeAccessRole.isPublished)}
                          size="small"
                          sx={{ 
                            backgroundColor: getStatusColor(employeeAccessRole.isPublished),
                            color: getStatusTextColor(employeeAccessRole.isPublished),
                            border: '1px solid',
                            borderColor: `${getStatusTextColor(employeeAccessRole.isPublished)}20`,
                            height: '24px',
                            minWidth: 'fit-content',
                            '& .MuiChip-label': {
                              fontSize: '13px',
                              padding: '0 8px',
                              fontWeight: 500,
                              whiteSpace: 'nowrap'
                            }
                          }}
                        />
                      </Box>
                      
                      <RoleDetailsGrid>
                        {employeeAccessRole.publishedAt && employeeAccessRole.isPublished && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Published: {toLocalDate(employeeAccessRole.publishedAt)?.toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}
                      </RoleDetailsGrid>

                      <RoleButtons>
                        {employeeAccessRole.isPublished ? (
                          <IconButton
                            onClick={() => handlePublishToggle(employeeAccessRole.id, employeeAccessRole.isPublished)}
                            color="primary"
                            size="small"
                            title="Unpublish individual role"
                          >
                            <FaCloudDownloadAlt />
                          </IconButton>
                        ) : (
                          <IconButton
                            onClick={() => handlePublishToggle(employeeAccessRole.id, employeeAccessRole.isPublished)}
                            color="primary"
                            size="small"
                            title="Publish individual role"
                          >
                            <FaCloudUploadAlt />
                          </IconButton>
                        )}
                        <IconButton
                          onClick={() => handleEditEmployee(employeeAccessRole)}
                          color="primary"
                          size="small"
                          title="Edit individual role"
                        >
                          <FaEdit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteClick(employeeAccessRole)}
                          color="error"
                          size="small"
                          title="Delete individual role"
                        >
                          <FaTrash />
                        </IconButton>
                      </RoleButtons>
                    </RoleCardContent>
                  </RoleCard>
                ))}
              </RolesGrid>
            )
          )}
        </MainContainer>
      </Content>

      {/* Assign/Edit Employee Role Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSaveRole}
        title={modalMode === 'assign' ? 'Assign Individual Role' : 'Edit Individual Role'}
        submitText={modalMode === 'assign' ? 'Assign Role' : 'Update Role'}
        submitDisabled={loading || (modalMode === 'assign' ? (!selectedEmployee || !selectedRole) : !selectedRole)}
        maxWidth="md"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {modalMode === 'assign' ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Select Individual
              </Typography>
              <EmployeeSearch
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                label="Search for individual"
                placeholder="Type to search..."
                sx={{ width: '100%' }}
                height="56px"
                showSelectedAvatar={true}
                disabledEmployeeIds={assignedEmployeeIds}
              />
              <FormHelperText>
                Only individuals without assigned roles are shown
              </FormHelperText>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Individual
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300'
              }}>
                <EmployeeAvatar 
                  employee={selectedEmployee}
                  size={48}
                />
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>
                    {selectedEmployee?.fullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedEmployee?.email}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Box>
            <FormControl fullWidth>
              <InputLabel>Select Role</InputLabel>
              <Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                label="Select Role"
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: role.color }}>
                        <DynamicIcon iconName={role.icon} />
                      </Box>
                      {role.displayName}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {modalMode === 'assign'
                  ? 'Choose the role to assign to the selected individual'
                  : 'Choose the new role for this individual'
                }
              </FormHelperText>
            </FormControl>
          </Box>

          {((modalMode === 'assign' && selectedEmployee && selectedRole) || 
            (modalMode === 'edit' && selectedRole && availableRoles.find(r => r.id === selectedRole))) && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300'
            }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preview:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <EmployeeAvatar 
                  employee={selectedEmployee}
                  size={40}
                  sx={{ bgcolor: availableRoles.find(r => r.id === selectedRole)?.color }}
                />
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>
                    {selectedEmployee?.fullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedEmployee?.email}
                  </Typography>
                  <Chip
                    label={availableRoles.find(r => r.id === selectedRole)?.displayName}
                    variant="outlined"
                    icon={<DynamicIcon iconName={availableRoles.find(r => r.id === selectedRole)?.icon} />}
                    size="small"
                    sx={{ 
                      mt: 0.5,
                      borderColor: availableRoles.find(r => r.id === selectedRole)?.color,
                      color: availableRoles.find(r => r.id === selectedRole)?.color,
                      backgroundColor: 'transparent',
                      fontWeight: 500,
                      fontSize: '12px',
                      '& .MuiChip-icon': {
                        color: availableRoles.find(r => r.id === selectedRole)?.color
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, employeeAccessRole: null, deleting: false })}
        onConfirm={handleDelete}
        title="Delete Individual Role"
        message={
          deleteModal.employeeAccessRole 
            ? `Are you sure you want to remove the role "${deleteModal.employeeAccessRole.role.displayName}" from "${deleteModal.employeeAccessRole.employee.fullName}"? This action cannot be undone.`
            : ""
        }
        loading={deleteModal.deleting}
      />
    </Container>
  );
};

export default EmployeeRoles;